import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SEOHead from '@/components/seo/SEOHead';
import Breadcrumbs from '@/components/seo/Breadcrumbs';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { 
  Building2, MapPin, Phone, Mail, UserCheck, School, 
  Send, MessageSquare, CheckCircle2, Clock 
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  createContactPageSchema, 
  createLocalBusinessSchema, 
  createBreadcrumbListSchema, 
  createSpeakable 
} from '@/utils/schemaGenerator';

export default function ContactPage() {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    queryType: 'general',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isKioskHighlighted, setIsKioskHighlighted] = useState(false);
  const [isBulkHighlighted, setIsBulkHighlighted] = useState(false);

  useEffect(() => {
    if (location.hash === '#kiosk-setup' || location.hash === '#institutional-kiosk') {
      setIsKioskHighlighted(true);
      setIsBulkHighlighted(false);
      setFormData(prev => ({ ...prev, queryType: 'kiosk' }));
      setTimeout(() => {
        const el = document.getElementById('kiosk-setup');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else if (location.hash === '#bulk-printing' || location.hash === '#bulk-printing-services' || location.hash === '#bulk') {
      setIsBulkHighlighted(true);
      setIsKioskHighlighted(false);
      setFormData(prev => ({ ...prev, queryType: 'bulk' }));
      setTimeout(() => {
        const el = document.getElementById('bulk-printing');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [location]);

  const breadcrumbItems = [
    { label: 'Contact Us', url: '/contact-us' }
  ];

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Us | Inchpaper Print-on-Demand Platform",
    "description": "Get in touch with Inchpaper Private Limited for bulk document printing, institutional kiosk setups, or customer support.",
    "url": "https://print.inchpaper.com/contact-us",
    "mainEntity": {
      "@type": "Organization",
      "name": "Inchpaper Private Limited",
      "url": "https://print.inchpaper.com",
      "logo": "https://print.inchpaper.com/inchpaper-printezy.png",
      "email": "info@inchpaper.com",
      "telephone": "+91-7703860982",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "575/7, 1st Floor, Inderpuri, Jacobpura",
        "addressLocality": "Gurgaon",
        "addressRegion": "Haryana",
        "postalCode": "122001",
        "addressCountry": "IN"
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+91-7834860982",
          "contactType": "Bulk Printing Services",
          "contactOption": "Mr. Surender Kohli"
        },
        {
          "@type": "ContactPoint",
          "telephone": "+91-7703860982",
          "contactType": "Institutional Kiosk Setup",
          "contactOption": "Mr. Pulak Agarwal"
        }
      ]
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Please enter your name and phone number');
      return;
    }
    setSubmitted(true);
    toast.success('Thank you! Your message has been received. Our team will contact you shortly.');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between antialiased">
      <SEOHead 
        title="Contact Us | Inchpaper Print-on-Demand Platform"
        description="Contact Inchpaper Private Limited for bulk printing services, institutional kiosk setups, or store inquiries in Gurgaon and Sohna, Haryana."
        keywords="inchpaper contact, inchpaper phone number, bulk printing enquiry, kiosk setup inchpaper, print center contact"
        canonical="https://print.inchpaper.com/contact-us"
        schemas={[
          createContactPageSchema(),
          createLocalBusinessSchema({
            name: "Inchpaper Head Office & Kiosk Helpdesk",
            url: "https://print.inchpaper.com/contact-us"
          }),
          createBreadcrumbListSchema([
            { name: "Home", url: "/" },
            { name: "Contact Us", url: "/contact-us" }
          ]),
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Contact Inchpaper",
            "speakable": createSpeakable(["h1", "p"])
          }
        ]}
      />

      <Navbar />

      <main className="grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Page Header */}
        <div className="mb-10 text-center max-w-3xl mx-auto space-y-2">
          <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            Contact Us
          </h1>
          <p className="text-slate-600 text-sm sm:text-base">
            Have questions about our cloud printing platform, bulk order rates, or setting up a kiosk at your institution? Reach out to us below.
          </p>
        </div>

        {/* Top Contact Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          
          {/* Head Office */}
          <div className="bg-white p-6 rounded-3xl border border-slate-200/90 shadow-xs space-y-4 relative overflow-hidden">
            <div className="w-10 h-10 rounded-xl bg-red-100/80 flex items-center justify-center text-[#7d0909]">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[11px] font-bold text-red-700 uppercase tracking-wider">Head Office</span>
              <h2 className="text-lg font-bold text-slate-900 mt-0.5">Inchpaper Private Limited</h2>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed font-medium">
              575/7, 1st Floor, Inderpuri, Jacobpura, Gurgaon 122001, Haryana, India
            </p>
            <div className="pt-2 border-t border-slate-100 space-y-1.5 text-xs text-slate-700 font-medium">
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-[#7d0909]" />
                <a href="tel:+917703860982" className="hover:text-red-700 transition-colors">+91-7703860982</a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-[#7d0909]" />
                <a href="mailto:info@inchpaper.com" className="hover:text-red-700 transition-colors">info@inchpaper.com</a>
              </div>
            </div>
          </div>

          {/* Bulk Printing */}
          <div 
            id="bulk-printing"
            className={`bg-white p-6 rounded-3xl border transition-all duration-300 shadow-xs space-y-4 ${
              isBulkHighlighted 
                ? 'border-2 border-[#7d0909] ring-4 ring-red-100 shadow-lg scale-[1.02]' 
                : 'border-slate-200/90'
            }`}
          >
            <div className="w-10 h-10 rounded-xl bg-red-100/80 flex items-center justify-center text-[#7d0909]">
              <UserCheck className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[11px] font-bold text-red-700 uppercase tracking-wider">Bulk Printing Services</span>
              <h2 className="text-lg font-bold text-slate-900 mt-0.5">Mr. Surender Kohli</h2>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              For large volume corporate documents, bulk project reports, thesis printing, and special business rates.
            </p>
            <div className="pt-2 border-t border-slate-100 space-y-1.5 text-xs text-slate-700 font-medium">
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-[#7d0909]" />
                <a href="tel:+917834860982" className="hover:text-red-700 transition-colors">+91-7834860982</a>
              </div>
              <div className="flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
                <a href="https://wa.me/917834860982" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-700 transition-colors">WhatsApp Direct</a>
              </div>
            </div>
          </div>

          {/* Institutional Setup */}
          <div 
            id="kiosk-setup" 
            className={`bg-white p-6 rounded-3xl border transition-all duration-300 shadow-xs space-y-4 ${
              isKioskHighlighted 
                ? 'border-2 border-[#7d0909] ring-4 ring-red-100 shadow-lg scale-[1.02]' 
                : 'border-slate-200/90'
            }`}
          >
            <div className="w-10 h-10 rounded-xl bg-red-100/80 flex items-center justify-center text-[#7d0909]">
              <School className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[11px] font-bold text-red-700 uppercase tracking-wider">Institutional Kiosk Set-up</span>
              <h2 className="text-lg font-bold text-slate-900 mt-0.5">Mr. Pulak Agarwal</h2>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Install automated self-service Inchpaper print kiosks at your college campus, university, or corporate office.
            </p>
            <div className="pt-2 border-t border-slate-100 space-y-1.5 text-xs text-slate-700 font-medium">
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-[#7d0909]" />
                <a href="tel:+917703860982" className="hover:text-red-700 transition-colors">+91-7703860982</a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-[#7d0909]" />
                <a href="mailto:info@inchpaper.com" className="hover:text-red-700 transition-colors">info@inchpaper.com</a>
              </div>
            </div>
          </div>

        </div>

        {/* Print Center Locations */}
        <div className="bg-white p-6 sm:p-10 rounded-3xl border border-slate-200/90 shadow-xs mb-12">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Our Print Centers</h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">Visit our active self-service cloud print kiosks for immediate document printing.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Sohna Centre */}
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200/80 space-y-3">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 bg-red-100 text-[#7d0909] text-xs font-bold rounded-full">Sohna Centre</span>
                <span className="text-xs text-slate-500 flex items-center gap-1 font-medium">
                  <Clock className="w-3.5 h-3.5 text-emerald-600" />
                  Mon-Sat: 9 am - 6 pm
                </span>
              </div>
              <h3 className="text-base font-bold text-slate-900">
                GD Goenka University, C Block, Ground Floor
              </h3>
              <p className="text-xs text-slate-600 font-medium leading-relaxed">
                GD Goenka University, C Block, Ground Floor, Sohna - Gurgaon Highway, Sohna, Haryana
              </p>
              <div className="pt-2">
                <a
                  href="https://maps.app.goo.gl/hcQdb9yTzxiHEDAK9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#7d0909] hover:underline"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  <span>View Google Maps Location →</span>
                </a>
              </div>
            </div>

            {/* Gurgaon Centre */}
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200/80 space-y-3">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 bg-red-100 text-[#7d0909] text-xs font-bold rounded-full">Gurgaon Centre</span>
                <span className="text-xs text-slate-500 flex items-center gap-1 font-medium">
                  <Clock className="w-3.5 h-3.5 text-emerald-600" />
                  Mon-Sat: 9 am - 6 pm
                </span>
              </div>
              <h3 className="text-base font-bold text-slate-900">
                Inderpuri, Jacobpura, Gurgaon
              </h3>
              <p className="text-xs text-slate-600 font-medium leading-relaxed">
                575/7, 1st Floor, Inderpuri, Jacobpura, Gurgaon, Haryana, 122001
              </p>
              <div className="pt-2">
                <a
                  href="https://maps.app.goo.gl/CBLs5VYGaPLRsH3s8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#7d0909] hover:underline"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  <span>View Google Maps Location →</span>
                </a>
              </div>
            </div>

          </div>
        </div>

        {/* Contact Form Section */}
        <div className="bg-white p-6 sm:p-10 rounded-3xl border border-slate-200/90 shadow-xs max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900">Send Us a Message</h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">Fill in the details below and our support team will reach out to you.</p>
          </div>

          {submitted ? (
            <div className="bg-emerald-50 border border-emerald-200 p-8 rounded-2xl text-center space-y-3">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
              <h3 className="text-xl font-bold text-slate-900">Message Sent Successfully!</h3>
              <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto">
                Thank you for reaching out to Inchpaper. Our representative will contact you via phone or WhatsApp shortly.
              </p>
              <Button 
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="mt-4 rounded-xl text-xs font-semibold"
              >
                Send Another Message
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Your Name *</label>
                  <input 
                    type="text"
                    required
                    placeholder="e.g. Rahul Sharma"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:ring-2 focus:ring-[#7d0909]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Phone Number / WhatsApp *</label>
                  <input 
                    type="tel"
                    required
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:ring-2 focus:ring-[#7d0909]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Email Address</label>
                  <input 
                    type="email"
                    placeholder="name@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:ring-2 focus:ring-[#7d0909]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Inquiry Type</label>
                  <select 
                    value={formData.queryType}
                    onChange={(e) => setFormData({ ...formData, queryType: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:ring-2 focus:ring-[#7d0909] bg-white"
                  >
                    <option value="general">General Support</option>
                    <option value="bulk">Bulk Printing Order</option>
                    <option value="kiosk">Institutional Kiosk Setup</option>
                    <option value="feedback">Feedback / Suggestion</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Your Message</label>
                <textarea 
                  rows={4}
                  placeholder="How can we help you today?"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:ring-2 focus:ring-[#7d0909]"
                />
              </div>

              <Button 
                type="submit"
                className="w-full bg-[#7d0909] hover:bg-[#5e0707] text-white py-3 rounded-xl font-bold text-sm shadow-xs flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                <span>Send Inquiry</span>
              </Button>
            </form>
          )}
        </div>

      </main>

      <Footer />
    </div>
  );
}
